/**
 * AI-2's diplomacy (ADR-0060): answering offers, and making its own.
 *
 * Every judgement goes through `appraise`, the same function that answers the player,
 * so an AI never offers what it would itself refuse, nor refuses what it would offer.
 * Each turn an AI may make one civilisation it has met one offer — an alliance against
 * its enemy, a technology swap or sale, open borders — and may demand tribute from one
 * it badly outmatches. A civilisation is not pestered: after an offer, the same AI waits
 * `diplomacy.aiOfferCooldown` turns before offering it anything again.
 */

import type { Content } from '../../../content/schema/index.js';
import { apply } from '../commands/index.js';
import type { GameEvent } from '../commands/types.js';
import { attitude } from '../diplomacy/attitude.js';
import { contactsOf } from '../diplomacy/borders.js';
import { appraise, bandRank, dealBlocker, resourceArms, techValue, canPromise } from '../diplomacy/deals.js';
import { resourceAccess } from '../resources/access.js';
import { resourceKnown, resourceUsed } from '../resources/known.js';
import { atWar, enemiesOf, militaryStrength } from '../diplomacy/index.js';
import { relationOf } from '../diplomacy/relation.js';
import { knows } from '../research/knowledge.js';
import { chance, type RngState } from '../rng/index.js';
import type { Civ, DealItem, GameState, Id } from '../state/types.js';
import { weightsFor } from './personality.js';

/** Technologies `from` could hand `to`: known to one, not the other, and ready to land. */
function tradeable(content: Content, from: Civ, to: Civ): number[] {
  return from.techs.filter(
    (t) => !knows(to, t) && content.technologies.get(t).prerequisites.every((p) => knows(to, p)),
  );
}

function patient(state: GameState, content: Content, civId: Id, other: Id): boolean {
  const relation = relationOf(state, civId, other);
  if (!relation) return false;
  const last = relation.a === civId ? relation.lastDealA : relation.lastDealB;
  return last < 0 || state.meta.turn - last >= content.rules.diplomacy.aiOfferCooldown;
}

/** Whether the other side would take it — asked of an AI directly, assumed reasonable of a human. */
function acceptable(state: GameState, content: Content, civId: Id, other: Id, give: DealItem[], get: DealItem[]): boolean {
  if (dealBlocker(state, content, civId, other, give, get)) return false;
  // A human is offered what an AI in their place would accept, so the offer is fair.
  return appraise(state, content, other, civId, give, get).accept;
}

function propose(state: GameState, content: Content, to: Id, give: DealItem[], get: DealItem[], demand = false): GameEvent[] {
  const result = apply(state, content, { t: 'ProposeDeal', to, give, get, demand });
  return result.ok ? result.value : [];
}

/**
 * A deal for a strategic resource `civId` lacks, needs to build soldiers, and `partner`
 * has (ADR-0063): one of its own the partner lacks in exchange, or gold a turn. Null when
 * there is nothing to ask for or nothing the partner would take.
 */
function resourceDeal(state: GameState, content: Content, civId: Id, partner: Id): { give: DealItem[]; get: DealItem[] } | null {
  const mine = resourceAccess(state, content, civId);
  const theirs = resourceAccess(state, content, partner);
  const them = state.civs[partner]!;
  for (const resource of content.resources.all) {
    const bit = 1 << resource.id;
    if (resource.class !== 'strategic' || ((mine.own | mine.traded) & bit) !== 0 || (theirs.own & bit) === 0) continue;
    if (!resourceArms(state, content, civId, resource.id)) continue;
    const get: DealItem[] = [{ kind: 'resource', resource: resource.id }];
    for (const spare of content.resources.all) {
      const spareBit = 1 << spare.id;
      if ((mine.own & spareBit) === 0 || ((theirs.own | theirs.traded) & spareBit) !== 0 || !resourceKnown(content, them, spare.id)) continue;
      if (!resourceUsed(content, spare.id)) continue;
      const give: DealItem[] = [{ kind: 'resource', resource: spare.id }];
      if (acceptable(state, content, civId, partner, give, get)) return { give, get };
    }
    const amount = Math.max(1, Math.round(weightsFor(content, state.civs[civId]).resourceBuyPerTurn));
    const give: DealItem[] = [{ kind: 'goldPerTurn', amount }];
    if (canPromise(state, content, civId, amount) && acceptable(state, content, civId, partner, give, get)) return { give, get };
  }
  return null;
}

export function directDeals(state: GameState, content: Content, civId: Id, rng: RngState | undefined): GameEvent[] {
  const events: GameEvent[] = [];
  const civ = state.civs[civId];
  if (!civ) return events;
  const ai = weightsFor(content, civ);

  // Offers waiting for an answer — only ever made to a player's seat, which the AI is
  // playing when the simulator runs with --all-ai.
  for (const offer of state.offers.filter((o) => o.to === civId)) {
    const verdict = appraise(state, content, civId, offer.from, offer.give, offer.get, offer.demand);
    const result = apply(state, content, { t: 'AnswerOffer', offerId: offer.id, accept: verdict.accept });
    if (result.ok) events.push(...result.value);
  }

  if (!rng) return events;
  const partners = contactsOf(state, civId).filter((id) => !atWar(state, civId, id) && patient(state, content, civId, id));
  if (partners.length === 0) return events;

  // ── a demand: tribute from one it badly outmatches and does not much like ─────────
  if (chance(rng, ai.demandChance)) {
    const ours = militaryStrength(state, content, civId);
    for (const target of partners) {
      const theirs = militaryStrength(state, content, target);
      if (ours < ai.demandStrengthRatio * Math.max(1, theirs)) continue;
      if (bandRank(content, attitude(state, content, civId, target).score) > 2) continue;
      const victim = state.civs[target]!;
      const gold = Math.floor(victim.treasury * ai.demandGoldShare);
      let item: DealItem | null = gold >= 20 ? { kind: 'gold', amount: gold } : null;
      if (!item) {
        const techs = tradeable(content, victim, civ).sort((a, b) => techValue(content, b) - techValue(content, a));
        if (techs.length > 0) item = { kind: 'tech', tech: techs[0]! };
      }
      if (item) return [...events, ...propose(state, content, target, [], [item], true)];
    }
  }

  if (!chance(rng, ai.offerChance)) return events;
  // Friends first.
  const ranked = partners
    .map((id) => ({ id, mood: attitude(state, content, civId, id).score }))
    .sort((a, b) => b.mood - a.mood || a.id - b.id);

  for (const { id: partner } of ranked) {
    const them = state.civs[partner]!;

    // ── an ally against an enemy ──────────────────────────────────────────────────
    for (const enemy of enemiesOf(state, civId)) {
      if (atWar(state, partner, enemy)) continue;
      const deal: DealItem[] = [{ kind: 'alliance', against: enemy }];
      if (acceptable(state, content, civId, partner, deal, [])) return [...events, ...propose(state, content, partner, deal, [])];
    }

    // Knowledge changes hands first, between those on decent terms (ADR-0073).
    const mine = tradeable(content, civ, them);
    const theirs = tradeable(content, them, civ);
    const decent = bandRank(content, attitude(state, content, civId, partner).score) >= 2;
    const knowledge = decent ? techDeal(state, content, civId, partner, mine, theirs) : null;
    if (knowledge) return [...events, ...propose(state, content, partner, knowledge.give, knowledge.get)];

    // ── a strategic resource it needs: a swap if it has one to spare, else gold ───
    const deal = resourceDeal(state, content, civId, partner);
    if (deal) return [...events, ...propose(state, content, partner, deal.give, deal.get)];

    // ── a technology swap, the closest in value ──────────────────────────────────
    let best: { give: number; get: number; gap: number } | null = null;
    for (const get of theirs) {
      for (const give of mine) {
        const gap = Math.abs(techValue(content, give) - techValue(content, get));
        if (!best || gap < best.gap || (gap === best.gap && get < best.get)) best = { give, get, gap };
      }
    }
    if (best) {
      const give: DealItem[] = [{ kind: 'tech', tech: best.give }];
      const get: DealItem[] = [{ kind: 'tech', tech: best.get }];
      const mineView = appraise(state, content, civId, partner, get, give);
      if (mineView.accept && acceptable(state, content, civId, partner, give, get)) {
        return [...events, ...propose(state, content, partner, give, get)];
      }
    }

    // ── buy a technology for gold, if rich enough ─────────────────────────────────
    for (const tech of [...theirs].sort((a, b) => techValue(content, a) - techValue(content, b))) {
      const price = Math.ceil(techValue(content, tech) * 0.8);
      if (price > civ.treasury * 0.6) continue;
      const give: DealItem[] = [{ kind: 'gold', amount: price }];
      const get: DealItem[] = [{ kind: 'tech', tech }];
      if (acceptable(state, content, civId, partner, give, get)) return [...events, ...propose(state, content, partner, give, get)];
      break;
    }

    // ── open borders, between friends ─────────────────────────────────────────────
    if (ai.openBordersValue > 0 && !relationOf(state, civId, partner)?.openBorders) {
      const deal: DealItem[] = [{ kind: 'openBorders' }];
      if (bandRank(content, attitude(state, content, civId, partner).score) >= 3 && acceptable(state, content, civId, partner, deal, [])) {
        return [...events, ...propose(state, content, partner, deal, [])];
      }
    }
  }
  return events;
}

/**
 * A technology deal worth offering (ADR-0073): the closest-valued swap both would take;
 * else a technology bought for gold it can spare; else one of its own sold for gold, or
 * gold a turn, the partner can pay. Null when there is nothing both would accept.
 */
function techDeal(
  state: GameState,
  content: Content,
  civId: Id,
  partner: Id,
  mine: number[],
  theirs: number[],
): { give: DealItem[]; get: DealItem[] } | null {
  const civ = state.civs[civId]!;
  const them = state.civs[partner]!;
  const ai = weightsFor(content, civ);
  const swaps: { give: number; get: number; gap: number }[] = [];
  for (const get of theirs) for (const give of mine) swaps.push({ give, get, gap: Math.abs(techValue(content, give) - techValue(content, get)) });
  swaps.sort((p, q) => p.gap - q.gap || p.get - q.get || p.give - q.give);
  for (const s of swaps.slice(0, 4)) {
    const give: DealItem[] = [{ kind: 'tech', tech: s.give }];
    const get: DealItem[] = [{ kind: 'tech', tech: s.get }];
    if (appraise(state, content, civId, partner, get, give).accept && acceptable(state, content, civId, partner, give, get)) return { give, get };
  }
  for (const tech of [...theirs].sort((a, b) => techValue(content, a) - techValue(content, b))) {
    const price = Math.ceil(techValue(content, tech) * 0.8);
    if (price > civ.treasury * 0.6) break;
    const give: DealItem[] = [{ kind: 'gold', amount: price }];
    const get: DealItem[] = [{ kind: 'tech', tech }];
    if (acceptable(state, content, civId, partner, give, get)) return { give, get };
  }
  // Selling: what the knowledge would cost them to research, at techSalePrice, in gold
  // now if they have it, otherwise spread over the payment term.
  const turns = content.rules.diplomacy.paymentTurns;
  for (const tech of [...mine].sort((a, b) => techValue(content, b) - techValue(content, a))) {
    const price = Math.ceil(techValue(content, tech) * ai.techSalePrice);
    const give: DealItem[] = [{ kind: 'tech', tech }];
    const pay: DealItem[] =
      price <= them.treasury * 0.6
        ? [{ kind: 'gold', amount: price }]
        : [{ kind: 'goldPerTurn', amount: Math.max(1, Math.ceil(price / turns)) }];
    if (appraise(state, content, civId, partner, pay, give).accept && acceptable(state, content, civId, partner, give, pay)) return { give, get: pay };
  }
  return null;
}
